import { useEffect, useRef } from 'react';

/**
 * Hook to handle keyboard events with automatic cleanup.
 * Deduplicates listeners across components by using a stable handler reference.
 *
 * @param key - The keyboard key to listen for (e.g., 'Escape', 'Enter')
 * @param handler - Callback to invoke when the key is pressed
 * @param enabled - Whether the listener is active (default: true)
 * @param options - Optional event behavior configuration
 *
 * @example
 * ```tsx
 * function Modal({ isOpen, onClose }) {
 *   // Close modal on Escape key, only when open
 *   useKeyboard('Escape', onClose, isOpen);
 *
 *   return isOpen ? <div>Modal content</div> : null;
 * }
 * ```
 *
 * @example
 * ```tsx
 * function SearchInput() {
 *   const [query, setQuery] = useState('');
 *
 *   // Submit search on Enter key, prevent default form submission
 *   useKeyboard('Enter', () => handleSearch(query), true, {
 *     preventDefault: true
 *   });
 *
 *   return <input value={query} onChange={e => setQuery(e.target.value)} />;
 * }
 * ```
 */
export function useKeyboard(
  key: string,
  handler: (event: KeyboardEvent) => void,
  enabled: boolean = true,
  options?: {
    preventDefault?: boolean;
    stopPropagation?: boolean;
  }
) {
  const savedHandler = useRef(handler);

  // Update handler ref on each render (avoids stale closures)
  useEffect(() => {
    savedHandler.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return;

    const listener = (event: KeyboardEvent) => {
      if (event.key === key) {
        if (options?.preventDefault) event.preventDefault();
        if (options?.stopPropagation) event.stopPropagation();
        savedHandler.current(event);
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [key, enabled, options?.preventDefault, options?.stopPropagation]);
}
